// companyService.js - Company Service Layer
import { companiesAPI, handleAPIError } from "./api";

// Company service functions
export const companyService = {
  // Get all companies with filters
  getAll: async (filters = {}) => {
    try {
      const response = await companiesAPI.getAll(filters);
      return {
        success: true,
        companies: response.data.companies,
        pagination: response.data.pagination,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
        companies: [],
      };
    }
  },

  // Get company by ID
  getById: async (id) => {
    try {
      const response = await companiesAPI.getById(id);
      return {
        success: true,
        company: response.data.company,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
        company: null,
      };
    }
  },

  // Create new company
  create: async (companyData) => {
    try {
      const response = await companiesAPI.create(companyData);
      return {
        success: true,
        company: response.data.company,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
      };
    }
  },

  // Update company
  update: async (id, companyData) => {
    try {
      const response = await companiesAPI.update(id, companyData);
      return {
        success: true,
        company: response.data.company,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
      };
    }
  },

  // Update company portal password
  updatePortalPassword: async (id, password) => {
    try {
      const response = await companiesAPI.updatePortalPassword(id, password);
      return {
        success: true,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
      };
    }
  },

  uploadCertificate: async (companyId, file, pfxPassword) => {
    try {
      return await companiesAPI.uploadCertificate(
        companyId,
        file,
        pfxPassword,
      );
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
      };
    }
  },

  deleteCertificate: async (companyId) => {
    try {
      const response = await companiesAPI.deleteCertificate(companyId);
      return {
        success: true,
        company: response.data?.company,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
        company: null,
      };
    }
  },

  // Contatos de e-mail do cliente (avisos de certificado)
  getContacts: async (companyId) => {
    try {
      const response = await companiesAPI.getContacts(companyId);
      return {
        success: true,
        contacts: response.data?.contacts || [],
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
        contacts: [],
      };
    }
  },

  addContact: async (companyId, contactData) => {
    try {
      const response = await companiesAPI.addContact(companyId, contactData);
      return {
        success: true,
        contact: response.data?.contact,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
        contact: null,
      };
    }
  },

  updateContact: async (companyId, contactId, contactData) => {
    try {
      const response = await companiesAPI.updateContact(
        companyId,
        contactId,
        contactData,
      );
      return {
        success: true,
        contact: response.data?.contact,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
        contact: null,
      };
    }
  },

  deleteContact: async (companyId, contactId) => {
    try {
      const response = await companiesAPI.deleteContact(companyId, contactId);
      return {
        success: true,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
      };
    }
  },

  // Delete company permanently
  delete: async (id) => {
    try {
      const response = await companiesAPI.delete(id);
      return {
        success: true,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
      };
    }
  },

  // Inactivate company (soft delete)
  inactivate: async (id) => {
    try {
      const response = await companiesAPI.inactivate(id);
      return {
        success: true,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
      };
    }
  },

  // Toggle company status
  toggleStatus: async (id) => {
    try {
      const response = await companiesAPI.toggleStatus(id);
      return {
        success: true,
        status: response.data.status,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
      };
    }
  },

  // Get inactivation logs
  getInactivationLogs: async (id) => {
    try {
      const response = await companiesAPI.getInactivationLogs(id);
      return {
        success: true,
        logs: response.data.logs || [],
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
        logs: [],
      };
    }
  },

  // Get latest download job details
  getLastDownloadJob: async (id) => {
    try {
      const response = await companiesAPI.getLastDownloadJob(id);
      return {
        success: true,
        job: response.data.job,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
        job: null,
      };
    }
  },

  // Get company users
  getUsers: async (id) => {
    try {
      const response = await companiesAPI.getUsers(id);
      return {
        success: true,
        users: response.data.users,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
        users: [],
      };
    }
  },

  // Link user to company
  linkUser: async (companyId, userId, accessLevel = "read") => {
    try {
      const response = await companiesAPI.linkUser(
        companyId,
        userId,
        accessLevel
      );
      return {
        success: true,
        link: response.data.link,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
      };
    }
  },

  // Unlink user from company
  unlinkUser: async (companyId, userId) => {
    try {
      const response = await companiesAPI.unlinkUser(companyId, userId);
      return {
        success: true,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
      };
    }
  },

  // Get company statistics
  getStatistics: async () => {
    try {
      const response = await companiesAPI.getStatistics();
      return {
        success: true,
        statistics: response.data.statistics,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
        statistics: null,
      };
    }
  },

  // Get company statistics for specific period
  getStatisticsForPeriod: async (startDate, endDate) => {
    try {
      const response = await companiesAPI.getStatisticsForPeriod(
        startDate,
        endDate
      );
      return {
        success: true,
        statistics: response.data.statistics,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
        statistics: [],
      };
    }
  },

  // Validate CNPJ format
  validateCNPJ: (cnpj) => {
    const cnpjRegex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;
    return cnpjRegex.test(cnpj);
  },

  // Format CNPJ
  formatCNPJ: (cnpj) => {
    const numbers = cnpj.replace(/\D/g, "");
    if (numbers.length === 14) {
      return numbers.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        "$1.$2.$3/$4-$5"
      );
    }
    return cnpj;
  },

  // Clean CNPJ (remove formatting)
  cleanCNPJ: (cnpj) => {
    return cnpj.replace(/\D/g, "");
  },

  // Import companies from Excel only
  importFromFile: async (file) => {
    try {
      const response = await companiesAPI.importFromFile(file);
      return {
        success: true,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
      };
    }
  },

  // Export companies to Excel
  exportToExcel: async (filters = {}) => {
    try {
      const result = await companiesAPI.exportToExcel(filters);
      return result;
    } catch (error) {
      return {
        success: false,
        message: handleAPIError(error),
      };
    }
  },
};
